import { useEffect, useRef, useState } from 'react';
import { resolveFestival } from './schedule';
import './festival.css';

// Vite only bundles these as separate lazy chunks; a theme downloads only while it is active.
const VARS = [
  '--festival-accent', '--festival-garland', '--festival-garland-dark', '--festival-garland-h',
  '--festival-lantern', '--festival-lantern-w', '--festival-lantern-h', '--festival-ground', '--festival-ground-h',
];

const themeLoaders = import.meta.glob('./themes/*.js');

const whenIdle = (fn) => {
  if ('requestIdleCallback' in window) {
    const id = requestIdleCallback(fn, { timeout: 2500 });
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(fn, 800);
  return () => clearTimeout(id);
};

const canAnimate = () =>
  !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches &&
  !navigator.connection?.saveData;

const density = () => {
  const weak =
    (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
  return weak ? 0.4 : window.innerWidth < 640 ? 0.6 : 1;
};

export default function FestivalLayer() {
  const [festival, setFestival] = useState(resolveFestival);
  const canvasRef = useRef(null);

  // Re-check when the tab comes back into view (covers midnight rollover, no timers needed)
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      setFestival((prev) => {
        const next = resolveFestival();
        return next?.id === prev?.id ? prev : next;
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const id = festival?.id;

  useEffect(() => {
    if (!id) return;
    const root = document.documentElement;
    let engine;
    let dead = false;
    root.dataset.festival = id;

    const cancel = whenIdle(async () => {
      const load = themeLoaders[`./themes/${id}.js`];
      if (!load) return;
      const theme = (await load()).default;
      if (dead) return;
      root.style.setProperty('--festival-accent', theme.accent);
      if (theme.garland) {
        root.style.setProperty('--festival-garland', theme.garland.url);
        root.style.setProperty('--festival-garland-dark', theme.garland.dark);
        root.style.setProperty('--festival-garland-h', theme.garland.h + 'px');
      }
      if (theme.lantern) {
        root.style.setProperty('--festival-lantern', theme.lantern.url);
        root.style.setProperty('--festival-lantern-w', theme.lantern.w + 'px');
        root.style.setProperty('--festival-lantern-h', theme.lantern.h + 'px');
      }
      if (theme.ground) {
        root.style.setProperty('--festival-ground', theme.ground.url);
        root.style.setProperty('--festival-ground-h', theme.ground.h + 'px');
      }
      root.dataset.festivalReady = '1';

      if (!canAnimate() || !canvasRef.current) return; // colors still apply, particles skipped
      const { createEngine } = await import('./engine');
      if (dead) return;
      engine = createEngine(canvasRef.current, theme, density());
      engine.start();
    });

    return () => {
      dead = true;
      cancel();
      engine?.destroy();
      delete root.dataset.festival;
      delete root.dataset.festivalReady;
      VARS.forEach((k) => root.style.removeProperty(k));
    };
  }, [id]);

  if (!id) return null;
  return <canvas ref={canvasRef} className="festival-canvas" aria-hidden="true" />;
}
